import { ViewportScroller } from '@angular/common';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivityInterface } from '@sport-activities/sdk';
import { format, parse } from 'date-fns';
import { Subscription } from 'rxjs';
import { APP_STORAGE } from '~/app.token';
import { getActivityDetailRouteName } from '~/attendee/helpers/activity/activity-detail-route-name.helper';
import { EventsByDate } from '~/attendee/interfaces/event.interface';
import { SEARCH_LOADER_KEY } from '~/attendee/service/paginated-search.service';
import { BannerSectionCms } from '~/cms/interfaces/homepage.cms.interface';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { LoaderService } from '~/core/services/loader/loader.service';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { LocaleService } from '~/i18n/services/locale.service';

import { PaginationTypeEnum } from './enums/pagination-type.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';

const SCROLL_POSITION = 'scrollPosition';

@Component({
  selector: 'app-attendee-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
})
export class AttendeePaginationComponent implements OnInit, OnDestroy {
  @Input() public bottomBanner?: BannerSectionCms;
  @Input() public hasLoadMoreButton?: boolean;
  @Input() public items: ActivityInterface[] | EventsByDate[];
  @Input() public itemsType = PaginationTypeEnum.ACTIVITIES;
  @Input() public topBanner?: BannerSectionCms;

  @Output() public readonly filterReset = new EventEmitter<void>();
  @Output()
  public loadMoreAction: EventEmitter<number> = new EventEmitter<number>();

  private readonly subscriptions = new Subscription();

  public readonly iconTypeEnum = VtmnPlayIconTypeEnum;
  public readonly tipsVariant = TipsVariantEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;

  public isLoading = true;
  public isMobileDevice: boolean;

  constructor(
    @Inject(APP_STORAGE) private readonly storage: Storage,
    private readonly loaderService: LoaderService,
    private readonly matchMediaService: MatchMediaService,
    private readonly localeService: LocaleService,
    private readonly viewportScroller: ViewportScroller
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    const scrollPosition = this.storage.getItem(SCROLL_POSITION);

    if (scrollPosition !== null) {
      setTimeout(
        () =>
          this.viewportScroller.scrollToPosition([0, Number(scrollPosition)]),
        200
      );
      this.storage.removeItem(SCROLL_POSITION);
    }
    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );

    this.subscriptions.add(
      this.loaderService.loading$.subscribe(() => {
        this.isLoading = this.loaderService.isLoading(SEARCH_LOADER_KEY);
      })
    );
  }

  public get isActivitiesPagination(): boolean {
    return this.itemsType === PaginationTypeEnum.ACTIVITIES;
  }

  public get paginationExtraClass(): string {
    return this.bottomBanner && 'mb-5 lg:mb-8';
  }

  public formatDate(date: string): string {
    const parsedDate = parse(date, 'yyyy-MM-dd', new Date());
    return format(parsedDate, 'PPPP', {
      locale: this.localeService.dateLocale,
    });
  }

  public getPath(activity: ActivityInterface): string {
    return getActivityDetailRouteName(activity.type, activity.sku);
  }

  // Mobile load more action
  public loadMore(): void {
    this.loadMoreAction.emit();
  }

  public resetFilters(): void {
    this.filterReset.emit();
  }

  public getScrollPosition() {
    const scrollPositionToSave = this.viewportScroller.getScrollPosition()[1];

    if (scrollPositionToSave !== 0) {
      this.storage.setItem(SCROLL_POSITION, scrollPositionToSave.toString());
    }
  }
}
